import React, { useState, useEffect, useContext, useRef } from 'react';
import useTranslate from '../../../../common/hooks/useTranslate';
import { filterDocuments, getDocsForSlice, getSlices, saveMultipleDocuments } from '../../../services/documentsService';
import getPeriodRange from '../../../../common/utility/getPeriodRange';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import removeFirstItemFromArray from '../../../../common/utility/removeFirstItemFromArray';
import {
  pagedDataGenerator,
  pagedFetchRequired,
  skipPagedResults,
} from '../../../ui/table/pagedDataResults/pagedDataGenerator';

import FilterCtx from '../../../../context/filterContext/filterCtx';
import DocsCtx from '../../../../context/docsContext/docsCtx';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { MODAL, POPUP } from '../../../../redux/types';
import { getUserSettings, saveUserSettings } from '../../../services/userService';

import { ISort } from '../../../home/types/ISort';

import { Grid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import {
  TreeList,
  mapTree,
  extendDataItem,
  TreeListSortChangeEvent,
} from '@progress/kendo-react-treelist';
import { DocumentSliceHierarchy } from './DocumentSliceHierarchy';
import { DocumentSliceNode } from './DocumentSliceNode';
import HeaderCell from '../cells/headerCell/headerCell';
import DateCell from '../cells/dateCell/dateCell';
import ChipsCell from '../cells/chipsCell/chipsCell';
import ReadCell from '../cells/readCell/readCell';
import ActionsCell from '../cells/actionsCell/actionsCell';
import DocumentNameCell from '../cells/documentNameCell/documentNameCell';
import DocumentFilters from '../filters/documentFilters';
import TableLoading from '../../../ui/loaders/tableLoader/tableLoading';
import TextCell from '../../../ui/table/cells/textCell/textCell';
import TreeListCell from '../cells/treelistCell/treeListCell';
import DocNameFilter from '../../../ui/filters/docNameFilter/docNameFilter';
import MobileNameCell from '../cells/mobileNameCell/mobileNameCell';
import ActionHeaderCell from '../cells/actionHeaderCell/actionHeaderCell';
import Button from '../../../ui/button/button';

import '../../../ui/table/table.scss';
import './documentsTable.scss';

const initialSort: ISort[] = [{ field: 'publishDate', dir: 'desc' }];
const subItemsField: string = 'children';
const expandField: string = 'expanded';

const DocumentsTable: React.FC = () => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sort, setSort] = useState<any>(initialSort);
  const [hierarchyData, setHierarchyData] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<any[]>([]);
  const [sliceArray, setSliceArray] = useState<string[]>([]);
  const [sliceSort, setSliceSort] = useState<SortDescriptor[]>([{ field: 'name', dir: 'asc' }]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [view, setView] = useState<string>('');

  const { t } = useTranslate();
  const dispatch = useDispatch();

  const filterCtx = useContext(FilterCtx);
  const docsCtx = useContext(DocsCtx);

  const isFetching = useRef(false);
  const [fetching, setFetching] = useState<boolean>(false);

  const tableRef = useRef(null);
  const mobileTableRef = useRef(null);
  const docHierarchyRef = useRef(new DocumentSliceHierarchy());

  const impersonate = useSelector((state: RootState) => state.impersonate);

  const pageSize = 100;
  const pageViewSize = 50;
  const [page, setPage] = useState<any>({ skip: 0, take: pageSize, includeCount: true });
  const take = pageSize * 3;

  const isMultipleDownloadOn = docsCtx.selectedItemsIds && docsCtx.selectedItemsIds.length > 1;
  const limit = 50;
  const limitReached = docsCtx.selectedItemsDocIds && docsCtx.selectedItemsDocIds.length >= limit;
  const isDocNumberShown = expanded.length;

  const onUncheck = (docId, id) => {
    docsCtx.setSelectedItemsDocIds((prevState) => {
      return removeFirstItemFromArray(prevState, docId);
    });
    docsCtx.setSelectedItemsIds((prevState) => {
      return prevState.filter((item) => item !== id);
    });
  };

  const handleDownloadClick = () => {
    let idsArray = docsCtx.selectedItemsDocIds.map((docId) => {
      return { id: docId };
    });

    saveMultipleDocuments(idsArray)
      .then(() => {
        dispatch({
          type: MODAL.SWITCH_MULTIPLE_DOWNLOAD_MODAL,
          payload: {
            text: t('DOCUMENTS_TABLE.MULTIPLE_DOWNLOAD_SUCCESSFUL'),
            error: false,
          },
        });
        docsCtx.setSelectedItemsDocIds([]);
        docsCtx.setSelectedItemsIds([]);
      })
      .catch((err) => {
        dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
      });
  };

  const handleSliceDownload = (dataArray) => {
    let idsArray = dataArray.map((dataItem) => {
      if (dataItem.documentId)
        return { id: dataItem.documentId };
    });

    saveMultipleDocuments(idsArray)
      .then(() => {
        dispatch({
          type: MODAL.SWITCH_MULTIPLE_DOWNLOAD_MODAL,
          payload: {
            text: t('DOCUMENTS_TABLE.MULTIPLE_DOWNLOAD_SUCCESSFUL'),
            error: false,
          },
        });
      })
      .catch((err) => {
        dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
      });
  };

  const onCheck = (docId, id) => {
    if (limitReached) {
      dispatch({ type: MODAL.REACHED_LIMIT_MODAL });
    } else {
      docsCtx.setSelectedItemsDocIds([...docsCtx.selectedItemsDocIds, docId]);
      docsCtx.setSelectedItemsIds([...docsCtx.selectedItemsIds, id]);
    }
  };

  const onCheckAll = (checked) => {
    let documentsToCheck = !isSlicerOn && (documents.slice(page.skip, page.skip + pageSize)).slice(0, limit);

    if (checked) {
      let selectedItemsDocIds = documentsToCheck.map((doc) => doc.documentId);
      let selectedItemsIds = documentsToCheck.map((doc) => doc.id);
      docsCtx.setSelectedItemsDocIds(selectedItemsDocIds);
      docsCtx.setSelectedItemsIds(selectedItemsIds);
    } else {
      docsCtx.setSelectedItemsDocIds([]);
      docsCtx.setSelectedItemsIds([]);
    }
  };

  const DocNameCell = (props) => <DocumentNameCell {...props} origin='documentsPage' />;
  const GridMobileNameCell = (props) => <MobileNameCell {...props} origin='documentsPage' />;
  const ReadDocCell = (props) => <ReadCell {...props} />;
  const ActCell = (props) => (
    <ActionsCell
      {...props}
      docId={props.dataItem.documentId}
      id={props.dataItem.id}
      onCheck={onCheck}
      onUncheck={onUncheck}
      multipleDownload={isMultipleDownloadOn}
      onMultipleDownloadClick={handleDownloadClick}
    />
  );
  const TextCellDC = (props) => <TextCell useTooltip={true} {...props} />;

  const ReadDocCellTreelist = (props) => <ReadCell {...props} treelist />;
  const ActCellTreelist = (props) => (
    <ActionsCell
      {...props}
      isDocNumberShown={isDocNumberShown}
      handleSliceDownload={handleSliceDownload}
      docId={props.dataItem.documentId}
      id={props.dataItem.id}
      treelist
      onCheck={onCheck}
      onUncheck={onUncheck}
      multipleDownload={isMultipleDownloadOn}
      onMultipleDownloadClick={handleDownloadClick}
    />
  );
  const TextCellTreelist = (props) => <TextCell useTooltip={true} {...props} treelist />;
  const DateCellTreelist = (props) => <DateCell {...props} treelist />;
  const ChipsCellTreelist = (props) => <ChipsCell useTooltip={true} {...props} treelist />;

  const columns = [
    {
      field: 'label',
      title: t('DOCUMENTS_TABLE.COLUMN_NAME'),
      expandable: true,
      headerCell: HeaderCell,
      cell: TreeListCell,
      width: '350px',
    },
    {
      field: 'publishDate',
      title: t('DOCUMENTS_TABLE.COLUMN_POSTED'),
      headerCell: HeaderCell,
      cell: DateCellTreelist,
      width: '100px',
    },
    {
      field: 'classifications',
      title: t('DOCUMENTS_TABLE.COLUMN_CLASSIFICATIONS'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: ChipsCellTreelist,
      width: '300px',
    },
    {
      field: 'fundName',
      title: t('DOCUMENTS_TABLE.COLUMN_FUND'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: TextCellTreelist,
      width: '300px',
    },
    {
      field: 'fundInvestorName', //'objectName',
      title: t('DOCUMENTS_TABLE.COLUMN_INVESTOR'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: TextCellTreelist,
      width: '300px',
    },
    {
      field: 'date',
      title: t('DOCUMENTS_TABLE.COLUMN_EFFECTIVE_DATE'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: DateCellTreelist,
      width: '100px',
    },
    {
      field: 'isRead',
      title: t('DOCUMENTS_TABLE.COLUMN_READ'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: ReadDocCellTreelist,
      width: '80px',
    },
    {
      field: 'actions',
      title: t('DOCUMENTS_TABLE.COLUMN_ACTIONS'),
      width: '150px',
      headerCell: (props) => <HeaderCell {...props} multipleDownload={isMultipleDownloadOn}
                                         onDownloadClick={handleDownloadClick} />,
      cell: ActCellTreelist,
      sortable: false,
    },
  ];

  const fetchDocuments = (page, sortField?, sortDir?) => {
    if (isFetching.current) return Promise.resolve();

    isFetching.current = true;
    setFetching(true);
    setLoading(true);
    const skip = skipPagedResults(page);
    const take = page.take * 3;

    const stored = sessionStorage.getItem('documentsPageView');

    if (stored === null) {
      return getUserSettings().then((settings) => {
        const documentPageViewSetting = settings.find((setting) => {
          return setting.name === 'documentsPageView';
        });
        if (documentPageViewSetting !== undefined) {
          docsCtx.setPageView(documentPageViewSetting.value === '1');
        }
        filterDocuments(
          filterCtx.docName,
          filterCtx.funds,
          filterCtx.investors,
          filterCtx.classifications,
          filterCtx.read,
          filterCtx.fromDate,
          filterCtx.toDate,
          skip,
          take,
          sortField,
          sortDir,
          filterCtx.publishChecked,
          filterCtx.effectiveChecked,
          null,
          filterCtx.period,
          page.includeCount,
        )
          .then((results) => {
            let pagedData = pagedDataGenerator(results, page, documents, skip);
            setDocuments(pagedData);
            setTotalCount(results.totalCount || 0);
            setLoading(false);
            setFetching(false);
          })
          .catch((err) => {
            dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
          })
          .finally(() => {
            isFetching.current = false;
            setLoading(false);
          });
      }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    } else {
      docsCtx.setPageView(stored === '1');
      return filterDocuments(
        filterCtx.docName,
        filterCtx.funds,
        filterCtx.investors,
        filterCtx.classifications,
        filterCtx.read,
        filterCtx.fromDate,
        filterCtx.toDate,
        skip,
        take,
        sortField,
        sortDir,
        filterCtx.publishChecked,
        filterCtx.effectiveChecked,
        null,
        filterCtx.period,
        page.includeCount,
      )
        .then((results) => {
          let pagedData = pagedDataGenerator(results, page, documents, skip);
          setDocuments(pagedData);
          setTotalCount(results.totalCount || 0);
          setLoading(false);
          setFetching(false);
        })
        .catch((err) => {
          dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
        })
        .finally(() => {
          isFetching.current = false;
          setLoading(false);
        });
    }
  };

  const handlePageChange = (event) => {
    if (docsCtx.pageView) {
      docsCtx.setSelectedItemsDocIds([]);
      docsCtx.setSelectedItemsIds([]);
    }
    setPage({ ...event.page, includeCount: false });
  };

  const handleSortChange = (e) => {
    setSort(e.sort);
  };

  const onExpandChange = (e): void => {
    const clickedNode: DocumentSliceNode = new DocumentSliceNode(e.dataItem.id, e.dataItem.name, e.dataItem.type, e.dataItem.parent);
    const ancestorsNodes: DocumentSliceNode[] = clickedNode.ancestors(clickedNode.children);

    if (sliceArray.length === 1 && sliceArray[0] === 'period') {
      let periodData = clickedNode.id.split('_');
      let periodNumber = periodData[0];
      let year = periodData[1];

      const range = getPeriodRange(+year, +periodNumber);

      filterDocuments(
        filterCtx.docName,
        filterCtx.funds,
        filterCtx.investors,
        filterCtx.classifications,
        filterCtx.read,
        filterCtx.fromDate,
        filterCtx.toDate,
        Math.max(0 - pageSize, 0),
        take,
        sort[0].field,
        sort[0].dir,
        filterCtx.publishChecked,
        filterCtx.effectiveChecked,
        null,
        range,
        true,
      ).then((res) => {
        docHierarchyRef.current.addChildren(res.data, sliceArray, clickedNode, docHierarchyRef.current._nodes);
        setExpanded(e.value ? expanded.filter((id) => id !== e.dataItem.nodeId) : [...expanded, e.dataItem.nodeId]);
      }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    } else {
      let formattedAncestors = [
        {
          objectId: clickedNode.id,
          type: clickedNode.type,
        },
      ];

      ancestorsNodes.forEach((ancestor) => {
        formattedAncestors.push({ objectId: ancestor.id, type: ancestor.type });
      });

      formattedAncestors = formattedAncestors.filter((ancestor) => {
        return ancestor.type !== 'period';
      });
      let periodData = [];
      let range = {};
      if (clickedNode.type === 'period') {
        periodData = clickedNode.id.split('_');
        let periodNumber = periodData[0];
        let year = periodData[1];
        range = getPeriodRange(+year, +periodNumber);
      }

      getDocsForSlice(
        formattedAncestors,
        filterCtx.docName,
        filterCtx.funds,
        filterCtx.investors,
        filterCtx.classifications,
        filterCtx.read,
        filterCtx.fromDate,
        filterCtx.toDate,
        Math.max(0 - pageSize, 0),
        take,
        sort[0].field,
        sort[0].dir,
        filterCtx.publishChecked,
        filterCtx.effectiveChecked,
        null,
        clickedNode.type === 'period' ? range : filterCtx.period,
        true,
      ).then((res) => {
        docHierarchyRef.current.addChildren(res.data.data, sliceArray, clickedNode, docHierarchyRef.current._nodes);
        setExpanded(e.value ? expanded.filter((id) => id !== e.dataItem.nodeId) : [...expanded, e.dataItem.nodeId]);
      }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }
  };

  const addExpandField = (dataTree: any[]): any => {
    return mapTree(dataTree, subItemsField, (item) =>
      extendDataItem(item, subItemsField, {
        [expandField]: expanded.includes(item.nodeId),
      }),
    );
  };

  const processData = (): any => {
    return hierarchyData ? addExpandField(hierarchyData) : [];
  };

  const numberOfIdSplits = (id: string) => {
    let fields = id.split('_');
    fields = fields.slice(1);
    return fields.length;
  };

  const handlePageViewChange = (pageView) => {
    docsCtx.setSelectedItemsDocIds([]);
    docsCtx.setSelectedItemsIds([]);
    setView(pageView ? 'page' : '');
    docsCtx.setPageView(pageView);
    const pageViewSetting = { name: 'documentsPageView', value: pageView ? '1' : '0' };
    sessionStorage.setItem('documentsPageView', pageView ? '1' : '0');
    saveUserSettings(pageViewSetting).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
  };

  useEffect(() => {
    if (filterCtx.slicerChoices.length === 0) {
      const handlePageChange = setTimeout(() => {
        if (pagedFetchRequired(page, documents)) {
          fetchDocuments(page, sort[0].field, sort[0].dir);
        }
      }, 200);
      return () => clearTimeout(handlePageChange);
    }
  }, [page]);

  useEffect(() => {
    if (filterCtx.slicerChoices.length === 0) {
      const fetch = setTimeout(() => {
        setDocuments([]);
        setTotalCount(0);
        setPage({ skip: 0, take: pageSize, includeCount: true });
        docsCtx.setSelectedItemsIds([]);
        docsCtx.setSelectedItemsDocIds([]);
        fetchDocuments({ skip: 0, take: pageSize, includeCount: true }, sort[0].field, sort[0].dir);
      }, 250);
      return () => clearTimeout(fetch);
    }

    setExpanded((prevState) => {
      return prevState.filter((id) => {
        return filterCtx.slicerChoices.includes('period') ? numberOfIdSplits(id) === sliceArray.length : numberOfIdSplits(id) !== sliceArray.length;
      });
    });
  }, [filterCtx, impersonate, sort]);

  useEffect(() => {
    const sliceArray = filterCtx.slicerChoices.map((item) => {
      if (item === 'fundName') {
        return 'fund';
      } else if (item === 'objectName') {
        return 'investor';
      } else if (item === 'classifications') {
        return 'classification';
      } else if (item === 'period') {
        return 'period';
      } else return null;
    });

    setLoading(true);

    setSliceArray(sliceArray);

    if (filterCtx.slicerChoices.length > 0) {
      getSlices(
        sliceArray,
        filterCtx.docName,
        filterCtx.funds,
        filterCtx.investors,
        filterCtx.classifications,
        filterCtx.read,
        filterCtx.fromDate,
        filterCtx.toDate,
        filterCtx.period,
        filterCtx.publishChecked,
        filterCtx.effectiveChecked,
      )
        .then((res) => {
          setHierarchyData(docHierarchyRef.current.generate(res.data, sliceArray));
        }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }))
        .finally(() => {
          setLoading(false);
        });
    }
  }, [filterCtx.slicerChoices]);

  const isSlicerOn = filterCtx.initialGroup.length !== 0 && !docsCtx.pageView;

  return (
    <>
      <div className='page-view-btn-container hidden-mobile'>
        <Button buttonStyle={`page-view-btn ${docsCtx.pageView === false ? 'active' : ''}`}
                handleClick={() => handlePageViewChange(false)}>
          <span>{t('DOCUMENTS_TABLE.REGULAR_VIEW')}</span>
        </Button>
        <Button buttonStyle={`page-view-btn ${docsCtx.pageView ? 'active' : ''}`}
                handleClick={() => handlePageViewChange(true)}>
          <span>{t('DOCUMENTS_TABLE.PAGE_VIEW')}</span>
        </Button>
      </div>
      <DocumentFilters className='hidden-mobile' pageView={view} />
      <div className='table documents-table hidden-desktop'>
        <div className='filters mobile-document-name-filter'>
          <DocNameFilter
            table='documents'
          />
        </div>
        <Grid
          data={documents.slice(page.skip, page.skip + pageSize)}
          pageSize={pageSize}
          skip={page.skip}
          total={totalCount}
          scrollable='virtual'
          sortable={{ allowUnsort: false }}
          sort={sort}
          rowHeight={40}
          dataItemKey={'id'}
          ref={mobileTableRef}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
        >
          <GridNoRecords>{fetching ? t('LOADING_DATA') : t('NO_RECORDS_AVAILABLE')}</GridNoRecords>
          <GridColumn
            field='label'
            title={t('DOCUMENTS_TABLE.COLUMN_NAME')}
            headerCell={HeaderCell}
            className='tableCell'
            cell={GridMobileNameCell}
          />
          <GridColumn
            field='classifications'
            title={t('DOCUMENTS_TABLE.COLUMN_CLASSIFICATIONS')}
            headerCell={HeaderCell}
            className='tableCell'
            cell={ChipsCell}
            sortable={false}
            minResizableWidth={180}
          />
          <GridColumn
            field='fundName'
            title={t('DOCUMENTS_TABLE.COLUMN_FUND')}
            headerCell={HeaderCell}
            className='tableCell'
            cell={TextCellDC}
            minResizableWidth={180}
          />
        </Grid>
        {loading && <TableLoading tableElement={mobileTableRef} />}
      </div>
      <div className='table documents-table hidden-mobile'>
        {isSlicerOn ? (
          <TreeList
            data={orderBy(processData(), sliceSort)}
            columns={columns}
            className={'documents-table-treelist'}
            subItemsField={subItemsField}
            expandField={expandField}
            onExpandChange={onExpandChange}
            sortable={true}
            sort={sliceSort}
            onSortChange={(sortEvent: TreeListSortChangeEvent) => {
              setSliceSort(sortEvent.sort);
            }}
            ref={tableRef}
          />
        ) : (
          <>
            <Grid
              data={documents.slice(page.skip, page.skip + (docsCtx.pageView ? pageViewSize : pageSize))}
              pageSize={docsCtx.pageView ? pageViewSize : pageSize}
              skip={page.skip}
              total={totalCount}
              scrollable={docsCtx.pageView ? undefined : 'virtual'}
              sortable={{ allowUnsort: false }}
              sort={sort}
              rowHeight={40}
              dataItemKey={'id'}
              resizable={true}
              ref={tableRef}
              onPageChange={handlePageChange}
              onSortChange={handleSortChange}
              pageable={docsCtx.pageView ? {
                buttonCount: 4,
              } : undefined}
            >
              <GridNoRecords>{fetching ? t('LOADING_DATA') : t('NO_RECORDS_AVAILABLE')}</GridNoRecords>
              <GridColumn
                field='label'
                title={t('DOCUMENTS_TABLE.COLUMN_NAME')}
                headerCell={HeaderCell}
                className='tableCell'
                cell={DocNameCell}
                width={300}
                minResizableWidth={180}
              />
              <GridColumn
                field='publishDate'
                title={t('DOCUMENTS_TABLE.COLUMN_POSTED')}
                headerCell={HeaderCell}
                cell={(props) => <DateCell {...props} />}
                width={150}
                minResizableWidth={120}
              />
              <GridColumn
                field='classifications'
                title={t('DOCUMENTS_TABLE.COLUMN_CLASSIFICATIONS')}
                headerCell={HeaderCell}
                className='tableCell'
                cell={(props) => <ChipsCell useTooltip={true} {...props} />}
                sortable={false}
                minResizableWidth={180}
              />
              <GridColumn
                field='fundName'
                title={t('DOCUMENTS_TABLE.COLUMN_FUND')}
                headerCell={HeaderCell}
                className='tableCell'
                cell={TextCellDC}
                minResizableWidth={180}
              />
              <GridColumn
                field='fundInvestorName'
                title={t('DOCUMENTS_TABLE.COLUMN_INVESTOR')}
                headerCell={HeaderCell}
                className='tableCell'
                cell={TextCellDC}
                minResizableWidth={180}
              />
              <GridColumn
                field='date'
                title={t('DOCUMENTS_TABLE.COLUMN_EFFECTIVE_DATE')}
                headerCell={HeaderCell}
                cell={(props) => <DateCell {...props} />}
                width={150}
                minResizableWidth={120}
              />
              <GridColumn
                field='isRead'
                title={t('DOCUMENTS_TABLE.COLUMN_READ')}
                headerCell={HeaderCell}
                className='tableCell'
                cell={ReadDocCell}
                width={80}
                minResizableWidth={100}
              />
              <GridColumn
                field='actions'
                title={t('DOCUMENTS_TABLE.COLUMN_ACTIONS')}
                headerCell={(props) => <ActionHeaderCell {...props} multipleDownload={isMultipleDownloadOn}
                                                         onDownloadClick={handleDownloadClick} limit={limit}
                                                         totalNumOfDocuments={docsCtx.pageView ?
                                                           documents.slice(page.skip, page.skip + (pageViewSize)).length :
                                                           documents.length}
                                                         onCheck={onCheckAll} />}
                cell={ActCell}
                width={155}
                sortable={false}
              />
            </Grid>
            {!loading && <div className='documents-total-label'><span
              className='bold'>{`${t('WIDGET.TOTAL')} ${t('DOCUMENTS_TABLE.LABEL_NUMBER_OF_DOCUMENTS')}: `}</span>{totalCount}
            </div>}
          </>
        )}
        {loading && <TableLoading tableElement={tableRef} />}
      </div>
    </>
  );
};

export default DocumentsTable;
